/**
 * Cached Circulate/Lend product context.
 *
 * The backend's /circulate/context decides which of the two circulate-
 * hosted products this account is entitled to ("circulate" / "lend") —
 * the fact that shapes the sidebar (Circulate vs Lend entry) and the
 * host pages. The shell and both pages share ONE fetch per session via
 * this module-level cache (the Stage 71/72/74 module-singleton
 * precedent); pages that need a fresh read (retry buttons) pass force.
 */
import { api } from "./api.js";

export interface CirculateContext {
	enabled: boolean;
	products: string[];
	client_id?: string;
	guard_level: number;
	staff_id?: string;
	staff_name?: string;
	level: number;
}

let cached: Promise<CirculateContext> | null = null;

export function getCirculateContext(force = false): Promise<CirculateContext> {
	if (!cached || force) {
		cached = (api.getCirculateContext() as Promise<CirculateContext>).catch((e) => {
			cached = null; // don't cache failures — the next caller retries
			throw e;
		});
	}
	return cached;
}

/** The entitled product set; [] when disabled, unauthenticated, or errored. */
export async function circulateProducts(): Promise<string[]> {
	try {
		const ctx = await getCirculateContext();
		return ctx.enabled ? (ctx.products ?? []) : [];
	} catch {
		return [];
	}
}

/**
 * Renders a COS staff user id as the UUID Lend's checkout/checkin bodies
 * actually accept for `staffId` (which circulate stores verbatim in
 * `checkout_staff_id` / `checkin_staff_id` / `id_verified_by` — plain
 * `UUID NULL` columns with no foreign key; see circulate
 * `db/migrations/000080_lend_initial_schema`). COS's staff identity is the
 * legacy integer `claims.AuthUserID` (this is what `CirculateContext.staff_id`
 * already is), and circulate has no crosswalk from that integer to a real
 * staff UUID. This mirrors the platform's existing tenant convention
 * (`ClientIDToCirculateUUID`'s "a0000000-…") under a different leading byte
 * so the two synthetic id spaces can never be mistaken for one another — it
 * lets a later reader decode which COS user this was, without asserting that
 * a real circulate staff identity exists for them.
 *
 * GUESS, not a confirmed convention: flag for sign-off before this is
 * load-bearing anywhere circulate might one day resolve `staffId` against a
 * real identity (there is no such resolution today — these columns are
 * write-only attribution markers).
 */
export function lendStaffAttesterUUID(staffId: string | undefined | null): string | undefined {
	if (!staffId) return undefined;
	const id = Number(staffId);
	if (!Number.isInteger(id) || id <= 0) return undefined;
	return `b0000000-0000-0000-0000-${String(id).padStart(12, "0")}`;
}

/**
 * The current staff member's Lend attester id, or undefined when it can't
 * be determined — never throws, so a context-fetch hiccup degrades to "no
 * attester" (the same as today) rather than breaking a checkout/checkin.
 */
export async function getLendAttesterUUID(): Promise<string | undefined> {
	try {
		const ctx = await getCirculateContext();
		return lendStaffAttesterUUID(ctx.staff_id);
	} catch {
		return undefined;
	}
}
